import type { ChangeEvent } from 'react';
import { useMemo } from 'react';
import {
  handleImageUpload,
  removeImageById,
  createImagePreviews,
  type ReviewImageFile,
} from '@skin-seoul/service/src/utils/review/imageUtils';
import { Section } from './common/SharedStyles';
import { SPACING } from './common/constants';
import {
  renderProductIntro,
  renderRatingSection,
  renderSkinInfoSection,
  renderReviewContentSection,
  renderTitleSection,
  renderImageUploadSection,
  renderRecommendationSection,
  renderNicknameSection,
  renderEmailSection,
  renderTermsSection,
  renderSubmitSection,
  ReviewRewardBanner,
} from './common/formHelpers';
import type { ReviewFormProps } from '@skin-seoul/service';

const MobileSection = ({ children, style }: { children: React.ReactNode; style?: React.CSSProperties }) => (
  <Section style={{ marginBottom: SPACING['2xl'], ...style }}>{children}</Section>
);

interface ReviewWriteFormProps extends ReviewFormProps {
  variant: 'mobile' | 'pc';
}

export const ReviewWriteForm = ({
  formData,
  formActions,
  options,
  productInfo,
  isSubmitDisabled,
  onSubmit,
  variant,
}: ReviewWriteFormProps) => {
  const images = formData.images as ReviewImageFile[];

  const handleImageChange = (e: ChangeEvent<HTMLInputElement>) => {
    handleImageUpload(e, images, formActions.setImages as (images: ReviewImageFile[]) => void);
  };

  const handleRemoveImage = (id: string) => {
    removeImageById(id, images, formActions.setImages as (images: ReviewImageFile[]) => void);
  };

  const imagePreviews = useMemo(() => createImagePreviews(images), [images]);

  const SectionComponent = variant === 'mobile' ? MobileSection : undefined;

  return (
    <>
      <div className="w-full py-4">
        <ReviewRewardBanner />
      </div>
      {renderProductIntro({ productInfo })}
      <div className="w-full h-px bg-neutral-100 shrink-0 my-4 tablet:my-8" />
      <div className="flex flex-col gap-10 tablet:gap-6 px-[2px] tablet:px-0 items-start w-full">
        {renderRatingSection({
          rating: formData.rating,
          setRating: formActions.setRating,
          SectionComponent,
        })}
        {renderTitleSection({
          title: formData.title,
          setTitle: formActions.setTitle,
        })}
        {renderReviewContentSection({
          content: formData.content,
          setContent: formActions.setContent,
        })}
        {renderImageUploadSection({
          images,
          imagePreviews,
          handleImageChange,
          handleRemoveImage,
        })}
        {renderRecommendationSection({
          recommend: formData.recommend,
          setRecommend: formActions.setRecommend,
        })}
        {renderSkinInfoSection({
          skinType: formData.skinType,
          setSkinType: formActions.setSkinType,
          skinConcern: formData.skinConcern,
          setSkinConcern: formActions.setSkinConcern,
          skinTypeOptions: options.skinTypeOptions,
          skinConcernOptions: options.skinConcernOptions,
        })}
        {renderNicknameSection({
          nickname: formData.nickname,
          setNickname: formActions.setNickname,
        })}
        {renderEmailSection({
          email: formData.email,
          setEmail: formActions.setEmail,
        })}
        {renderTermsSection()}
        {renderSubmitSection({ isSubmitDisabled, onSubmit })}
      </div>
    </>
  );
};
